import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, Newspaper, RefreshCw, Check, X, Pencil, Trash2, Star, Pin,
  ExternalLink, Settings, Plus, Search, Loader2, EyeOff,
} from "lucide-react";
import { safeFetch } from "../utils/safeFetch";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["연애·관계", "재혼·동반자", "싱글라이프", "사회·문화", "해외 트렌드"];
const STATUS_TABS = [
  { key: "pending_approval", label: "승인 대기" },
  { key: "approved", label: "승인됨" },
  { key: "excluded", label: "제외됨" },
  { key: "unpublished", label: "공개 중지" },
  { key: "전체", label: "전체" },
];
const STATUS_LABEL = {
  collected: "수집됨", pending_approval: "승인 대기", approved: "승인됨",
  published: "게시됨", excluded: "제외됨", unpublished: "공개 중지",
};

function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s); if (isNaN(d)) return "";
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

export default function AdminNewsPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("pending_approval");
  const [articles, setArticles] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: tab, limit: "50" });
      if (query) params.set("search", query);
      const r = await safeFetch(`${API_URL}/api/admin/news?${params.toString()}`, { headers: authHeaders });
      const d = await r.json();
      setArticles(d.articles || []);
      setCounts(d.counts || {});
      setTotal(d.total || 0);
    } catch { toast.error("목록을 불러오지 못했습니다."); }
    setLoading(false);
  }, [tab, query, token]);

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [tab, query, token]);

  if (!authLoading && (!isAuthenticated || user?.role !== "admin")) { navigate("/"); return null; }

  const act = async (id, path, body) => {
    try {
      const r = await safeFetch(`${API_URL}/api/admin/news/${id}/${path}`, { method: "POST", headers: authHeaders });
      if (r.ok) { toast.success("처리되었습니다."); load(); }
      else toast.error("처리 실패");
    } catch { toast.error("처리 실패"); }
  };

  const patch = async (id, body, silent) => {
    try {
      const r = await safeFetch(`${API_URL}/api/admin/news/${id}`, {
        method: "PATCH", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (r.ok) { if (!silent) toast.success("저장되었습니다."); load(); return true; }
      toast.error("저장 실패"); return false;
    } catch { toast.error("저장 실패"); return false; }
  };

  const del = async (id) => {
    if (!window.confirm("이 기사를 삭제할까요?")) return;
    try {
      const r = await safeFetch(`${API_URL}/api/admin/news/${id}`, { method: "DELETE", headers: authHeaders });
      if (r.ok) { toast.success("삭제되었습니다."); load(); }
    } catch { toast.error("삭제 실패"); }
  };

  const runCollect = async () => {
    setCollecting(true);
    try {
      const r = await safeFetch(`${API_URL}/api/admin/news/collect`, { method: "POST", headers: authHeaders });
      if (r.ok) toast.success("뉴스 수집을 시작했습니다. 30초~1분 후 새로고침 해주세요.");
    } catch { toast.error("수집 시작 실패"); }
    setTimeout(() => setCollecting(false), 2000);
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4">
        <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 mb-4" data-testid="news-admin-back">
          <ArrowLeft className="w-4 h-4" /> 관리자 홈
        </button>

        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-burgundy-600" strokeWidth={2.2} />
            <h1 className="text-2xl font-bold text-stone-900">중년싱글 뉴스 관리</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/admin/community-news")} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white border border-stone-200 text-sm font-semibold text-stone-600 hover:border-burgundy-300" data-testid="cnews-manage-link">
              <Newspaper className="w-4 h-4" /> 카테고리 뉴스
            </button>
            <button onClick={() => setShowSettings(true)} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white border border-stone-200 text-sm font-semibold text-stone-600 hover:border-burgundy-300" data-testid="news-settings-btn">
              <Settings className="w-4 h-4" /> 설정 · 키워드
            </button>
            <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white border border-burgundy-300 text-sm font-semibold text-burgundy-700 hover:bg-burgundy-50" data-testid="news-manual-add-btn">
              <Plus className="w-4 h-4" /> 기사 직접 추가
            </button>
            <button onClick={runCollect} disabled={collecting} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-700 text-white text-sm font-bold disabled:opacity-60" data-testid="news-collect-now-btn">
              <RefreshCw className={`w-4 h-4 ${collecting ? "animate-spin" : ""}`} /> 지금 수집 실행
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="news-admin-tabs">
          {STATUS_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${tab === t.key ? "bg-burgundy-600 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-burgundy-300"}`}
              data-testid={`news-tab-${t.key}`}>
              {t.label}{t.key !== "전체" && counts[t.key] != null ? ` (${counts[t.key]})` : ""}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) setQuery(search.trim()); }}
            placeholder="제목·매체 검색" className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:border-burgundy-400"
            data-testid="news-admin-search" />
        </div>

        {/* List */}
        <div className="space-y-2">
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-burgundy-400" /></div>
          ) : articles.length === 0 ? (
            <div className="py-12 text-center text-sm text-stone-400 bg-white rounded-xl border border-stone-200">기사가 없습니다.</div>
          ) : articles.map((a) => (
            <div key={a.id} className="bg-white rounded-xl border border-stone-200 p-3.5" data-testid={`news-admin-item-${a.id}`}>
              <div className="flex items-start gap-2 flex-wrap">
                <span className="text-[11px] font-semibold text-burgundy-600">[{a.category}]</span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">{a.region === "domestic" ? "국내" : "해외"}</span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">{STATUS_LABEL[a.status] || a.status}</span>
                {a.is_featured && <span className="text-[11px] px-1.5 py-0.5 rounded bg-burgundy-600 text-white">주목</span>}
                {a.is_pinned && <span className="text-[11px] px-1.5 py-0.5 rounded bg-stone-800 text-white">고정</span>}
                <span className="text-[11px] text-stone-400 ml-auto">관련성 {a.relevance_score} · 신뢰 {a.trust_score}{a.is_ad ? " · 광고성" : ""}</span>
              </div>
              <a href={a.url} target="_blank" rel="noopener noreferrer" className="mt-1.5 flex items-center gap-1.5 text-[15px] font-bold text-stone-800 hover:text-burgundy-700 hover:underline">
                {a.korean_title || a.original_title} <ExternalLink className="w-3.5 h-3.5 text-stone-300" />
              </a>
              {a.korean_title && a.original_title !== a.korean_title && <p className="text-[12px] text-stone-400 mt-0.5">원문: {a.original_title}</p>}
              {a.ai_summary && <p className="text-[12.5px] text-stone-500 mt-1">{a.ai_summary}</p>}
              <div className="flex items-center gap-1.5 mt-1 text-[12px] text-stone-400">
                <span>{a.source_name}</span><span>·</span><span>{fmtDate(a.published_date)}</span>
              </div>

              <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                {a.status !== "approved" && (
                  <button onClick={() => act(a.id, "approve")} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-[12px] font-semibold hover:bg-emerald-100" data-testid={`news-approve-${a.id}`}><Check className="w-3.5 h-3.5" />승인</button>
                )}
                {a.status === "approved" && (
                  <button onClick={() => act(a.id, "unpublish")} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-stone-100 text-stone-600 text-[12px] font-semibold hover:bg-stone-200" data-testid={`news-unpublish-${a.id}`}><EyeOff className="w-3.5 h-3.5" />공개중지</button>
                )}
                <button onClick={() => setEditing(a)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-stone-100 text-stone-600 text-[12px] font-semibold hover:bg-stone-200" data-testid={`news-edit-${a.id}`}><Pencil className="w-3.5 h-3.5" />수정</button>
                <button onClick={() => act(a.id, "exclude")} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-stone-100 text-stone-600 text-[12px] font-semibold hover:bg-stone-200" data-testid={`news-exclude-${a.id}`}><X className="w-3.5 h-3.5" />제외</button>
                <button onClick={() => patch(a.id, { is_featured: !a.is_featured })} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold ${a.is_featured ? "bg-burgundy-100 text-burgundy-700" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid={`news-feature-${a.id}`}><Star className="w-3.5 h-3.5" />주목</button>
                <button onClick={() => patch(a.id, { is_pinned: !a.is_pinned })} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold ${a.is_pinned ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid={`news-pin-${a.id}`}><Pin className="w-3.5 h-3.5" />고정</button>
                <button onClick={() => del(a.id)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 text-[12px] font-semibold hover:bg-red-100 ml-auto" data-testid={`news-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" />삭제</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && <EditModal article={editing} onClose={() => setEditing(null)} onSave={async (body) => { const ok = await patch(editing.id, body); if (ok) setEditing(null); }} />}
      {showSettings && <SettingsModal token={token} onClose={() => setShowSettings(false)} />}
      {showAdd && <AddModal token={token} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); setTab("approved"); load(); }} />}
    </div>
  );
}

function EditModal({ article, onClose, onSave }) {
  const [f, setF] = useState({
    korean_title: article.korean_title || "", source_name: article.source_name || "",
    published_date: (article.published_date || "").slice(0, 10), url: article.url || "",
    category: article.category || CATEGORIES[0], status: article.status || "approved",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="news-edit-modal">
        <h3 className="text-lg font-bold text-stone-900 mb-4">기사 수정</h3>
        <div className="space-y-3">
          <Field label="홈페이지 제목"><input value={f.korean_title} onChange={(e) => set("korean_title", e.target.value)} className="ncinput" data-testid="edit-korean-title" /></Field>
          <Field label="매체명"><input value={f.source_name} onChange={(e) => set("source_name", e.target.value)} className="ncinput" data-testid="edit-source" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="게시 날짜"><input type="date" value={f.published_date} onChange={(e) => set("published_date", e.target.value)} className="ncinput" data-testid="edit-date" /></Field>
            <Field label="카테고리">
              <select value={f.category} onChange={(e) => set("category", e.target.value)} className="ncinput" data-testid="edit-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="원문 링크"><input value={f.url} onChange={(e) => set("url", e.target.value)} className="ncinput" data-testid="edit-url" /></Field>
          <Field label="상태">
            <select value={f.status} onChange={(e) => set("status", e.target.value)} className="ncinput" data-testid="edit-status">
              {["pending_approval", "approved", "excluded", "unpublished"].map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </Field>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm">취소</button>
          <button onClick={() => onSave({ ...f, published_date: f.published_date ? new Date(f.published_date).toISOString() : article.published_date })} className="flex-1 py-2.5 rounded-xl bg-burgundy-600 text-white font-bold text-sm" data-testid="edit-save">저장</button>
        </div>
      </div>
    </div>
  );
}

function SettingsModal({ token, onClose }) {
  const [s, setS] = useState(null);
  const [dk, setDk] = useState(""); const [fk, setFk] = useState(""); const [ek, setEk] = useState("");
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    safeFetch(`${API_URL}/api/admin/news/settings`, { headers }).then((r) => r.json()).then((d) => setS(d.settings)).catch(() => {});
  }, []);
  const save = async () => {
    try {
      const r = await safeFetch(`${API_URL}/api/admin/news/settings`, { method: "PUT", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(s) });
      if (r.ok) { toast.success("설정이 저장되었습니다."); onClose(); }
    } catch { toast.error("저장 실패"); }
  };
  if (!s) return null;
  const addKw = (field, val, setter) => { const v = val.trim(); if (v && !s[field].includes(v)) { setS({ ...s, [field]: [...s[field], v] }); setter(""); } };
  const rmKw = (field, v) => setS({ ...s, [field]: s[field].filter((x) => x !== v) });
  const Chips = ({ field }) => (
    <div className="flex flex-wrap gap-1.5">
      {s[field].map((k) => (
        <span key={k} className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-[12px] px-2 py-1 rounded-full">
          {k}<button onClick={() => rmKw(field, k)} className="text-stone-400 hover:text-red-500"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  );
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="news-settings-modal">
        <h3 className="text-lg font-bold text-stone-900 mb-4">뉴스 수집 설정</h3>
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2"><p className="text-sm font-bold text-stone-700">국내 키워드</p></div>
            <div className="flex gap-2 mb-2">
              <input value={dk} onChange={(e) => setDk(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) addKw("domestic_keywords", dk, setDk); }} placeholder="키워드 추가" className="ncinput flex-1" data-testid="news-add-domestic-kw" />
              <button onClick={() => addKw("domestic_keywords", dk, setDk)} className="px-3 rounded-lg bg-stone-800 text-white"><Plus className="w-4 h-4" /></button>
            </div>
            <Chips field="domestic_keywords" />
          </div>
          <div>
            <p className="text-sm font-bold text-stone-700 mb-2">해외 키워드</p>
            <div className="flex gap-2 mb-2">
              <input value={fk} onChange={(e) => setFk(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) addKw("foreign_keywords", fk, setFk); }} placeholder="Add keyword" className="ncinput flex-1" data-testid="news-add-foreign-kw" />
              <button onClick={() => addKw("foreign_keywords", fk, setFk)} className="px-3 rounded-lg bg-stone-800 text-white"><Plus className="w-4 h-4" /></button>
            </div>
            <Chips field="foreign_keywords" />
          </div>
          <div>
            <p className="text-sm font-bold text-stone-700 mb-2">제외 키워드</p>
            <div className="flex gap-2 mb-2">
              <input value={ek} onChange={(e) => setEk(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) addKw("exclude_keywords", ek, setEk); }} placeholder="제외할 키워드" className="ncinput flex-1" data-testid="news-add-exclude-kw" />
              <button onClick={() => addKw("exclude_keywords", ek, setEk)} className="px-3 rounded-lg bg-stone-800 text-white"><Plus className="w-4 h-4" /></button>
            </div>
            <Chips field="exclude_keywords" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="첫 화면 노출 수"><input type="number" min="1" max="10" value={s.home_count} onChange={(e) => setS({ ...s, home_count: parseInt(e.target.value) || 5 })} className="ncinput" data-testid="news-home-count" /></Field>
            <Field label="국내 비율 (0~1)"><input type="number" step="0.1" min="0" max="1" value={s.domestic_ratio} onChange={(e) => setS({ ...s, domestic_ratio: parseFloat(e.target.value) || 0.6 })} className="ncinput" /></Field>
            <Field label="최소 관련성 점수"><input type="number" min="0" max="100" value={s.min_relevance} onChange={(e) => setS({ ...s, min_relevance: parseInt(e.target.value) || 55 })} className="ncinput" /></Field>
            <Field label="보관 기간(일)"><input type="number" min="7" value={s.retention_days} onChange={(e) => setS({ ...s, retention_days: parseInt(e.target.value) || 180 })} className="ncinput" /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input type="checkbox" checked={s.auto_translate} onChange={(e) => setS({ ...s, auto_translate: e.target.checked })} /> 해외 기사 자동 번역(한국어 제목 생성) 사용
          </label>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm">취소</button>
          <button onClick={save} className="flex-1 py-2.5 rounded-xl bg-burgundy-600 text-white font-bold text-sm" data-testid="news-settings-save">저장</button>
        </div>
      </div>
    </div>
  );
}

function AddModal({ token, onClose, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [f, setF] = useState({
    korean_title: "", source_name: "", ai_summary: "", published_date: "",
    category: CATEGORIES[0], region: "domestic", is_featured: false, is_pinned: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const fetchMeta = async () => {
    const u = url.trim();
    if (!/^https?:\/\//.test(u)) { toast.error("올바른 링크(URL)를 입력해 주세요."); return; }
    setFetching(true);
    try {
      const r = await safeFetch(`${API_URL}/api/admin/news/fetch-meta`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify({ url: u }),
      });
      const d = await r.json();
      const m = d.meta || {};
      setF((p) => ({
        ...p,
        korean_title: m.korean_title || p.korean_title,
        source_name: m.source_name || p.source_name,
        ai_summary: m.ai_summary || p.ai_summary,
        published_date: (m.published_date || "").slice(0, 10) || p.published_date,
        region: m.region || p.region,
      }));
      setFetched(true);
      if (d.success) toast.success("링크에서 정보를 불러왔습니다. 확인 후 저장해 주세요.");
      else toast.warning(d.message || "일부 정보를 가져오지 못했습니다. 직접 입력해 주세요.");
    } catch { toast.error("정보를 불러오지 못했습니다. 직접 입력해 주세요."); setFetched(true); }
    setFetching(false);
  };

  const save = async () => {
    if (!/^https?:\/\//.test(url.trim())) { toast.error("올바른 링크(URL)를 입력해 주세요."); return; }
    if (!f.korean_title.trim()) { toast.error("제목을 입력해 주세요."); return; }
    setSaving(true);
    try {
      const body = {
        url: url.trim(), korean_title: f.korean_title.trim(), source_name: f.source_name.trim(),
        ai_summary: f.ai_summary.trim(), category: f.category, region: f.region,
        is_featured: f.is_featured, is_pinned: f.is_pinned,
        published_date: f.published_date ? new Date(f.published_date).toISOString() : "",
      };
      const r = await safeFetch(`${API_URL}/api/admin/news/manual`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (r.ok) { toast.success("기사가 등록되었습니다."); onSaved(); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "등록 실패"); }
    } catch { toast.error("등록 실패"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="news-add-modal">
        <h3 className="text-lg font-bold text-stone-900 mb-1">기사 직접 추가</h3>
        <p className="text-[12.5px] text-stone-400 mb-4">링크를 붙여넣고 <b>불러오기</b>를 누르면 제목·매체·요약을 자동으로 채웁니다. 이후 수정 후 저장하세요.</p>
        <div className="space-y-3">
          <Field label="기사 링크 (URL)">
            <div className="flex gap-2">
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." className="ncinput flex-1" data-testid="news-add-url" />
              <button onClick={fetchMeta} disabled={fetching} className="px-3.5 rounded-lg bg-stone-800 text-white text-sm font-semibold whitespace-nowrap disabled:opacity-60 flex items-center gap-1.5" data-testid="news-add-fetch">
                {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} 불러오기
              </button>
            </div>
          </Field>
          <Field label="제목"><input value={f.korean_title} onChange={(e) => set("korean_title", e.target.value)} placeholder="홈페이지에 표시될 제목" className="ncinput" data-testid="news-add-title" /></Field>
          <Field label="요약 (선택)"><textarea value={f.ai_summary} onChange={(e) => set("ai_summary", e.target.value)} rows={2} placeholder="1~2문장 요약" className="ncinput resize-none" data-testid="news-add-summary" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="매체명"><input value={f.source_name} onChange={(e) => set("source_name", e.target.value)} className="ncinput" data-testid="news-add-source" /></Field>
            <Field label="게시 날짜"><input type="date" value={f.published_date} onChange={(e) => set("published_date", e.target.value)} className="ncinput" data-testid="news-add-date" /></Field>
            <Field label="카테고리">
              <select value={f.category} onChange={(e) => set("category", e.target.value)} className="ncinput" data-testid="news-add-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="지역">
              <select value={f.region} onChange={(e) => set("region", e.target.value)} className="ncinput" data-testid="news-add-region">
                <option value="domestic">국내</option>
                <option value="foreign">해외</option>
              </select>
            </Field>
          </div>
          <div className="flex items-center gap-4 pt-1">
            <label className="flex items-center gap-1.5 text-sm text-stone-700 cursor-pointer">
              <input type="checkbox" checked={f.is_featured} onChange={(e) => set("is_featured", e.target.checked)} data-testid="news-add-featured" /> <Star className="w-3.5 h-3.5 text-burgundy-500" /> 주목
            </label>
            <label className="flex items-center gap-1.5 text-sm text-stone-700 cursor-pointer">
              <input type="checkbox" checked={f.is_pinned} onChange={(e) => set("is_pinned", e.target.checked)} data-testid="news-add-pinned" /> <Pin className="w-3.5 h-3.5 text-stone-700" /> 고정
            </label>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm">취소</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-burgundy-600 text-white font-bold text-sm disabled:opacity-60 flex items-center justify-center gap-1.5" data-testid="news-add-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} 등록 (바로 공개)
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-semibold text-stone-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
